import React, { useEffect, useState } from 'react';

import { NoticePopup, NoticePopupLabels } from './types';

// 이미지 영역 높이(px). 모든 슬라이드 공통
export const DEFAULT_IMAGE_HEIGHT = 740;
// 팝업 최대 너비(px). 가로로 긴 이미지가 과하게 커지지 않도록 제한
export const DEFAULT_MAX_WIDTH = 960;
// 비율을 아직 모를 때 쓰는 팝업 너비(px)
export const DEFAULT_POPUP_WIDTH = 480;
// 이미지 영역 좌우/상하 여백(px). Tailwind p-5 와 동일
const SLIDE_PADDING = 20;
// 도트 + 하단 버튼이 쓰는 높이(px). 이미지 영역 높이 상한 계산에 사용
const FOOTER_HEIGHT = 90;
// 팝업이 화면 높이에서 차지할 수 있는 비율
const VIEWPORT_HEIGHT_RATIO = 0.94;

const DEFAULT_LABELS: NoticePopupLabels = {
  hideFor7Days: '7일간 보지 않기',
  hideForever: '다시 보지 않기',
  close: '닫기',
  prev: '이전 공지',
  next: '다음 공지',
  goToSlide: (popup) => `${popup.imageAlt} 보기`,
};

type Props = {
  popups: NoticePopup[];
  activeIndex: number;
  onChangeIndex: (index: number) => void;
  // 팝업 전체 닫기
  onClose: () => void;
  // '다시 보지 않기' / '7일간 보지 않기' 는 현재 슬라이드만 대상
  onHideCurrentForever: () => void;
  onHideCurrentFor7Days?: () => void;
  // 이미지 영역 높이(px). 팝업 너비는 현재 슬라이드 이미지 비율로 자동 계산
  imageHeight?: number;
  // 팝업 최대 너비(px). 가로로 긴 이미지는 이 너비에 맞춰 높이가 줄어듦
  maxWidth?: number;
  labels?: Partial<NoticePopupLabels>;
  // 브랜드 색상 (활성 도트 / 닫기 버튼)
  accentColor?: string;
  accentHoverColor?: string;
  // 이미지 클릭 시 동작 (미전달 시 클릭 불가)
  onImageClick?: (popup: NoticePopup) => void;
};

const NoticePopupSlider = ({
  popups,
  activeIndex,
  onChangeIndex,
  onClose,
  onHideCurrentForever,
  onHideCurrentFor7Days,
  imageHeight = DEFAULT_IMAGE_HEIGHT,
  maxWidth = DEFAULT_MAX_WIDTH,
  labels,
  accentColor = '#bc1a21',
  accentHoverColor = '#a01619',
  onImageClick,
}: Props) => {
  // 로드된 이미지의 가로/세로 비율 (config 의 aspectRatio 가 없을 때 사용)
  const [loadedRatios, setLoadedRatios] = useState<Record<string, number>>({});
  // 화면 높이 변화 추적 (이미지 영역 높이 상한 계산용)
  const [viewportHeight, setViewportHeight] = useState<number>(() =>
    typeof window === 'undefined' ? 0 : window.innerHeight,
  );

  const currentPopup = popups[activeIndex];

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const measureRatio = (popup: NoticePopup, image: HTMLImageElement | null) => {
    // 캐시된 이미지는 onLoad 가 오지 않을 수 있어 ref 시점에도 확인
    if (!image?.complete || !image.naturalHeight) return;

    const ratio = image.naturalWidth / image.naturalHeight;
    setLoadedRatios((prev) => (prev[popup.id] === ratio ? prev : { ...prev, [popup.id]: ratio }));
  };

  if (!currentPopup) return null;

  const t = { ...DEFAULT_LABELS, ...labels };
  const hasMultiple = popups.length > 1;
  const showHideFor7Days = Boolean(currentPopup.showHideFor7DaysButton && onHideCurrentFor7Days);

  // 현재 슬라이드 이미지를 최대 너비/높이 안에 꽉 채우고, 팝업 크기를 그 결과에 맞춤
  const currentRatio = loadedRatios[currentPopup.id] ?? currentPopup.aspectRatio;
  const availableHeight = viewportHeight
    ? Math.min(imageHeight, viewportHeight * VIEWPORT_HEIGHT_RATIO - FOOTER_HEIGHT - SLIDE_PADDING * 2)
    : imageHeight;
  const displayWidth = currentRatio ? Math.min(maxWidth - SLIDE_PADDING * 2, availableHeight * currentRatio) : 0;
  const displayHeight = currentRatio ? displayWidth / currentRatio : availableHeight;
  const popupWidth = currentRatio ? Math.round(displayWidth) + SLIDE_PADDING * 2 : DEFAULT_POPUP_WIDTH;

  // 양끝에서 순환
  const goPrev = () => onChangeIndex((activeIndex - 1 + popups.length) % popups.length);
  const goNext = () => onChangeIndex((activeIndex + 1) % popups.length);

  const arrowClassName =
    'absolute top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border-none bg-black/35 text-lg leading-none text-white transition-colors hover:bg-black/55';
  const footerButtonClassName = 'flex-1 border-none p-4 text-sm font-medium transition-colors';
  const secondaryButtonClassName = `${footerButtonClassName} border-r border-solid border-r-[#e5e7eb] bg-white text-[#6b7280] hover:bg-[#f9fafb]`;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-[2px]"
      onClick={onClose}>
      <div
        className="relative flex max-h-[94vh] max-w-[94vw] flex-col overflow-hidden rounded-xl bg-white shadow-[0_10px_40px_rgba(0,0,0,0.3)]"
        style={
          {
            width: popupWidth,
            '--notice-accent': accentColor,
            '--notice-accent-hover': accentHoverColor,
          } as React.CSSProperties
        }
        onClick={(e) => e.stopPropagation()}>
        <div
          className="relative shrink-0 overflow-hidden"
          style={{ height: Math.round(displayHeight) + SLIDE_PADDING * 2 }}>
          <div
            className="flex h-full transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}>
            {popups.map((popup) => (
              <div key={popup.id} className="flex flex-[0_0_100%] items-center justify-center overflow-hidden p-5">
                <img
                  src={popup.image}
                  alt={popup.imageAlt}
                  className={`block h-full w-auto max-w-full object-contain${onImageClick ? ' cursor-pointer' : ''}`}
                  ref={(image) => measureRatio(popup, image)}
                  onLoad={(event) => measureRatio(popup, event.currentTarget)}
                  onClick={onImageClick ? () => onImageClick(popup) : undefined}
                />
              </div>
            ))}
          </div>
          {hasMultiple && (
            <>
              <button type="button" aria-label={t.prev} className={`${arrowClassName} left-2`} onClick={goPrev}>
                ‹
              </button>
              <button type="button" aria-label={t.next} className={`${arrowClassName} right-2`} onClick={goNext}>
                ›
              </button>
            </>
          )}
        </div>
        {hasMultiple && (
          <div className="flex items-center justify-center gap-2 pb-3.5">
            {popups.map((popup, index) => (
              <button
                key={popup.id}
                type="button"
                aria-label={t.goToSlide(popup, index)}
                aria-current={index === activeIndex}
                className={`h-2 w-2 rounded-full border-none p-0 ${
                  index === activeIndex ? 'bg-[var(--notice-accent)]' : 'bg-gray-300'
                }`}
                onClick={() => onChangeIndex(index)}
              />
            ))}
          </div>
        )}
        <div className="flex border-t border-solid border-t-[#e5e7eb]">
          {showHideFor7Days && (
            <button type="button" className={secondaryButtonClassName} onClick={onHideCurrentFor7Days}>
              {t.hideFor7Days}
            </button>
          )}
          <button type="button" className={secondaryButtonClassName} onClick={onHideCurrentForever}>
            {t.hideForever}
          </button>
          <button
            type="button"
            className={`${footerButtonClassName} bg-[var(--notice-accent)] text-white hover:bg-[var(--notice-accent-hover)]`}
            onClick={onClose}>
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoticePopupSlider;
